import { useState, useCallback, useRef } from 'react';
import apiClient from '../services/api';

export function useAiModels() {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadModels = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/ai/models');
      setModels(response.data.models || []);
    } catch (err) {
      console.error('Failed to load AI models', err);
      setError('Failed to load AI models');
    } finally {
      setLoading(false);
    }
  }, []);

  return { models, loading, error, loadModels };
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export function useAiSession() {
  const activeRequestIdRef = useRef(null);
  const cancelledRef = useRef(false);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Live build status streamed from the background job:
  // { status, stage, detail, progress }
  const [jobStatus, setJobStatus] = useState(null);

  const newRequestId = () =>
    (window.crypto?.randomUUID ? window.crypto.randomUUID() : `req-${Date.now()}-${Math.random().toString(36).slice(2)}`);

  const pollJob = useCallback(async (jobId) => {
    // Poll until the background build finishes. Each poll is a fast request,
    // so even very long builds never hit proxy timeouts. Ceiling ~35 minutes.
    for (let i = 0; i < 800; i++) {
      if (cancelledRef.current) {
        const err = new Error('Cancelled by user');
        err.userCancelled = true;
        throw err;
      }
      let data;
      try {
        const response = await apiClient.get(`/ai/jobs/${jobId}`);
        data = response.data;
      } catch (pollErr) {
        // Transient poll failure (network blip) - retry
        await sleep(3000);
        continue;
      }
      setJobStatus(data);
      if (data.status === 'done') return data.session;
      if (data.status === 'cancelled') {
        const err = new Error('Cancelled by user');
        err.userCancelled = true;
        throw err;
      }
      if (data.status === 'error') {
        const err = new Error(data.error || 'Generation failed');
        err.detail = data.error || 'Generation failed';
        throw err;
      }
      await sleep(2500);
    }
    const err = new Error('Build timed out');
    err.detail = 'The build took too long. Please try again.';
    throw err;
  }, []);

  const runJob = useCallback(async (createJob, fallbackError) => {
    cancelledRef.current = false;
    const requestId = newRequestId();
    activeRequestIdRef.current = requestId;

    setLoading(true);
    setError(null);
    setJobStatus({ status: 'running', stage: 'Queued', detail: 'Contacting the GameOS build service', progress: 2 });
    try {
      const { data } = await createJob(requestId);
      const newSession = await pollJob(data.job_id);
      setSession(newSession);
      return newSession;
    } catch (err) {
      if (!err.userCancelled) {
        console.error(fallbackError, err);
        setError(err.detail || err.response?.data?.detail || fallbackError);
      }
      throw err;
    } finally {
      setLoading(false);
      setJobStatus(null);
      activeRequestIdRef.current = null;
    }
  }, [pollJob]);

  const startSession = useCallback(
    (initialPrompt, modelId, gameTitle, engine) =>
      runJob(
        (requestId) =>
          apiClient.post(
            '/ai/sessions/jobs',
            {
              initial_prompt: initialPrompt,
              model_id: modelId,
              title: gameTitle,
              engine: engine || null,
            },
            { headers: { 'X-GameOS-Request-Id': requestId } },
          ),
        'Failed to start AI session',
      ),
    [runJob],
  );

  const sendMessage = useCallback(
    (sessionId, message) =>
      runJob(
        (requestId) =>
          apiClient.post(
            `/ai/sessions/${sessionId}/message/jobs`,
            { message },
            { headers: { 'X-GameOS-Request-Id': requestId } },
          ),
        'Failed to send AI message',
      ),
    [runJob],
  );

  const restoreVersion = useCallback(async (sessionId, versionId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.post(
        `/ai/sessions/${sessionId}/versions/${versionId}/restore`,
        {}
      );
      setSession(response.data);
      return response.data;
    } catch (err) {
      console.error('Failed to restore version', err);
      setError(err.response?.data?.detail || 'Failed to restore version');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSessionByGameId = useCallback(async (gameId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`/ai/sessions/by-game/${gameId}`);
      setSession(response.data);
      return response.data;
    } catch (err) {
      console.error('Failed to load AI session for game', err);
      setError(err.response?.data?.detail || 'Failed to load AI session for this game');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSessionById = useCallback(async (sessionId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`/ai/sessions/${sessionId}`);
      setSession(response.data);
      return response.data;
    } catch (err) {
      console.error('Failed to load AI session', err);
      setError(err.response?.data?.detail || 'Failed to load AI session');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLatestSession = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/ai/sessions/latest');
      setSession(response.data);
      return response.data;
    } catch (err) {
      console.error('Failed to load latest AI session', err);
      setError(err.response?.data?.detail || 'Failed to load latest AI session');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const cancelCurrent = useCallback(() => {
    cancelledRef.current = true;
    const requestId = activeRequestIdRef.current;
    if (requestId) {
      // Tell the backend to abort the build and skip billing
      apiClient.post(`/ai/cancel/${requestId}`).catch(() => {});
      activeRequestIdRef.current = null;
    }
  }, []);

  const commitToGame = useCallback(async (sessionId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.post(
        `/ai/sessions/${sessionId}/commit-to-game`,
        {}
      );
      return response.data;
    } catch (err) {
      console.error('Failed to commit session to game', err);
      setError(err.response?.data?.detail || 'Failed to save game');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    session,
    loading,
    error,
    jobStatus,
    startSession,
    sendMessage,
    restoreVersion,
    commitToGame,
    setSession,
    loadSessionByGameId,
    loadSessionById,
    loadLatestSession,
    cancelCurrent,
  };
}
